import React, { useState } from 'react';
import { LOCAL_LOGIN, MIN_PASSWORD, sendPasswordReset, setPassword, signIn } from '../../services/auth';
import './Login.css';

/**
 * Staff sign-in — email + password on one screen, plus "Forgot password?".
 * SetPassword is where the invite / reset-password email links land.
 */

const useRunner = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
};

const PasswordInput = ({ id, value, onChange, autoComplete, placeholder, autoFocus }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="login-password">
      <input
        id={id}
        className="login-input"
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
      />
      <button type="button" className="login-show" onClick={() => setShow((v) => !v)} aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  );
};

const Shell = ({ children }) => (
  <div className="login">
    <div className="login-card">
      <img src="/cie-logo.png" alt="Canadian Insulation Experts" className="login-logo" />
      {children}
    </div>
  </div>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPasswordValue] = useState('');
  const [resetSent, setResetSent] = useState('');
  const { busy, error, run } = useRunner();

  return (
    <Shell>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(() => signIn(email, password));
        }}
      >
        <h1>Sign in</h1>
        <p className="login-sub">Canadian Insulation Experts field app</p>

        <label className="login-label" htmlFor="login-email">Work email</label>
        <input
          id="login-email"
          className="login-input"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          placeholder="you@canadianinsulationexperts.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
        />

        <label className="login-label" htmlFor="login-password">Password</label>
        <PasswordInput id="login-password" value={password} onChange={setPasswordValue} autoComplete="current-password" placeholder="Password" />

        {error && <p className="login-error" role="alert">{error}</p>}
        {resetSent && !error && (
          <p className="login-ok" role="status">
            If <strong>{resetSent}</strong> is a staff account, a link to set a new password is on its way.
          </p>
        )}

        <button type="submit" className="login-btn" disabled={busy || !email.trim() || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="login-links">
          <button
            type="button"
            className="login-link"
            disabled={busy}
            onClick={() =>
              run(async () => {
                setResetSent('');
                setResetSent(await sendPasswordReset(email));
              })
            }
          >
            Forgot password?
          </button>
        </div>
      </form>

      {LOCAL_LOGIN && <p className="login-local">Supabase not connected yet — shared admin login.</p>}
    </Shell>
  );
};

// from the invite or "forgot password" email
export const SetPassword = ({ user, linkType }) => {
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const { busy, error, run } = useRunner();

  return (
    <Shell>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(() => setPassword(password, confirm));
        }}
      >
        <h1>{linkType === 'invite' ? 'Welcome — set your password' : 'Set a new password'}</h1>
        <p className="login-sub">
          For <strong>{user && user.email}</strong>. At least {MIN_PASSWORD} characters.
        </p>

        <label className="login-label" htmlFor="new-password">New password</label>
        <PasswordInput id="new-password" value={password} onChange={setPasswordValue} autoComplete="new-password" placeholder="New password" autoFocus />

        <label className="login-label" htmlFor="confirm-password">Type it again</label>
        <PasswordInput id="confirm-password" value={confirm} onChange={setConfirm} autoComplete="new-password" placeholder="Same password" />

        {error && <p className="login-error" role="alert">{error}</p>}
        <button type="submit" className="login-btn" disabled={busy || !password || !confirm}>
          {busy ? 'Saving…' : 'Save password and sign in'}
        </button>
      </form>
    </Shell>
  );
};

export default Login;
